import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import './FeedbackModal.css';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedbackText: string, category?: string) => Promise<void>;
}

const FEEDBACK_CATEGORIES = [
  { value: '', label: 'Select a category' },
  { value: 'report-bug', label: 'Report a Bug' },
  { value: 'not-helpful', label: 'Response Not Helpful' },
  { value: 'other-issue', label: 'Other Issue' },
  { value: 'helpful', label: 'Helpful Response' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { colors } = useTheme();
  const [feedbackText, setFeedbackText] = useState('');
  const [category, setCategory] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFeedbackText('');
      setCategory('');
      setIsDropdownOpen(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleCategorySelect = (value: string) => {
    setCategory(value);
    setIsDropdownOpen(false);
  };

  const getSelectedCategoryLabel = () => {
    const selected = FEEDBACK_CATEGORIES.find(cat => cat.value === category);
    return selected ? selected.label : 'Select a category';
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim() || !category || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(feedbackText, category);
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="feedback-modal"
        style={{
          backgroundColor: colors.surface,
          borderColor: colors.border,
        }}
      >
        <div className="feedback-modal-header">
          <h2 style={{ color: colors.textPrimary }}>Send Feedback</h2>
          <button
            onClick={onClose}
            className="feedback-modal-close"
            style={{
              color: colors.textMuted,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="feedback-modal-body">
          <div className="feedback-category-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className="feedback-category-trigger"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{
                backgroundColor: colors.surfaceElevated,
                color: !category ? colors.textMuted : colors.textPrimary,
                borderColor: colors.border,
              }}
            >
              <span>{getSelectedCategoryLabel()}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              >
                <polyline points="4 6 8 10 12 6" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div
                className="feedback-category-options"
                style={{
                  backgroundColor: colors.surfaceElevated,
                  borderColor: colors.border,
                  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`,
                }}
              >
                {FEEDBACK_CATEGORIES.filter(cat => cat.value !== '').map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`feedback-category-option ${category === cat.value ? 'selected' : ''}`}
                    onClick={() => handleCategorySelect(cat.value)}
                    style={{
                      color: colors.textPrimary,
                      backgroundColor: category === cat.value ? colors.primary + '20' : 'transparent',
                    }}
                  >
                    {cat.label}
                    {category === cat.value && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke={colors.primary}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 8 6 11 13 4" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Share your feedback about this response..."
            className="feedback-modal-textarea"
            style={{
              backgroundColor: colors.surfaceElevated,
              color: colors.textPrimary,
              borderColor: colors.border,
            }}
            rows={5}
          />
        </div>

        <div className="feedback-modal-footer">
          <button
            onClick={onClose}
            className="feedback-modal-button feedback-modal-button-cancel"
            style={{
              backgroundColor: colors.surfaceElevated,
              color: colors.textPrimary,
              borderColor: colors.border,
            }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="feedback-modal-button feedback-modal-button-submit"
            style={{
              backgroundColor: feedbackText.trim() && category ? colors.primary : colors.surfaceElevated,
              color: feedbackText.trim() && category ? '#ffffff' : colors.textMuted,
              opacity: isSubmitting ? 0.6 : 1,
            }}
            disabled={!feedbackText.trim() || !category || isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
